import { NavLink, Outlet, useLocation } from 'react-router';
import { twMerge } from 'tailwind-merge';
import type { NavLink as SiteLink } from '@avh/shared';
import { useI18n } from '@/contexts/I18n';
import { usePublicSettings } from '@/contexts/Settings';

const LINK = 'rounded-md px-3 py-2 text-sm font-semibold text-brand-900 hover:bg-brand-50';
const SHOP_PATH = '/tickets/';

/** Solange die Einstellungen laden: wenigstens der Weg zur Website und zum Shop. */
const FALLBACK_MAIN: SiteLink[] = [{ label: 'Tickets', href: SHOP_PATH }];

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return twMerge(LINK, isActive ? 'bg-brand-50' : '');
}

/**
 * Kopf und Fuß wie auf den Seiten der Website (apps/api/src/website/templates.ts): gleiches Menü,
 * gleiche Fußzeile. Links zur Website sind normale Links (statische Seiten), der Shop bleibt in der App.
 */
function Header() {
  const { t, locale, setLocale } = useI18n();
  const settings = usePublicSettings();
  const { pathname } = useLocation();
  const shopActive = pathname.startsWith('/tickets') && !pathname.startsWith('/tickets/konto');
  const links = settings?.navigation.main ?? FALLBACK_MAIN;
  return (
    <header className="border-b-4 border-accent-500 bg-white">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
        <a href="/" className="flex items-center gap-3" aria-label={t.nav.home}>
          <img src="/logo-abz-hagenberg.svg" alt="" className="h-12 w-auto" width={196} height={108} />
          <span className="hidden text-base leading-tight font-bold text-brand-900 sm:block">
            Absolventenverband
            <br />
            Hagenberg
          </span>
        </a>
        <nav className="flex flex-wrap items-center gap-1" aria-label="Hauptnavigation">
          {links.map((link) =>
            link.href === SHOP_PATH ? (
              <NavLink key={link.href} to="/tickets" className={() => navLinkClass({ isActive: shopActive })} aria-current={shopActive ? 'page' : undefined}>
                {link.label}
              </NavLink>
            ) : (
              <a key={link.href} href={link.href} className={LINK}>
                {link.label}
              </a>
            ),
          )}
          <NavLink to="/tickets/konto" className={navLinkClass}>
            {t.nav.account}
          </NavLink>
          <button
            type="button"
            onClick={() => setLocale(locale === 'de' ? 'en' : 'de')}
            className="rounded-md px-3 py-2 text-sm font-semibold text-brand-600 hover:bg-brand-50"
            aria-label={t.nav.switchLanguageLabel}
          >
            {locale === 'de' ? 'EN' : 'DE'}
          </button>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  const { t } = useI18n();
  const settings = usePublicSettings();
  return (
    <footer className="mt-16 bg-brand-900 text-white">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:grid-cols-3">
        <div>
          <p className="text-lg font-bold">{settings?.orgName ?? 'Absolventenverband Hagenberg'}</p>
          {settings ? (
            <address className="mt-2 text-sm leading-relaxed text-white/80 not-italic">
              {settings.street}
              <br />
              {settings.zip} {settings.city}
            </address>
          ) : null}
        </div>
        <div className="text-sm">
          <p className="font-semibold text-sun-300">{t.footer.contact}</p>
          {settings ? (
            <a href={`mailto:${settings.contactEmail}`} className="mt-2 block text-white/90 underline-offset-4 hover:underline">
              {settings.contactEmail}
            </a>
          ) : null}
          <a href={SHOP_PATH} className="mt-1 block text-white/90 underline-offset-4 hover:underline">
            Ticketshop
          </a>
        </div>
        <nav className="flex flex-col gap-1 text-sm sm:items-end" aria-label="Rechtliches">
          {(settings?.navigation.footer ?? []).map((link) => (
            <a key={link.href} href={link.href} className="text-white/90 underline-offset-4 hover:underline">
              {link.label}
            </a>
          ))}
        </nav>
      </div>
    </footer>
  );
}

export function PublicLayout() {
  return (
    <div className="flex min-h-dvh flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

export function Container({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={twMerge('mx-auto w-full max-w-6xl px-4 py-8 sm:py-12', className)}>{children}</div>;
}
